import { create } from 'zustand'
import { get as apiGet, post, put, getToken, setToken, clearToken, ApiError } from '../lib/api'

export interface AuthUser {
  id: string
  email: string
  username: string
  avatarUrl: string | null
  street: string | null
  zip: string | null
  city: string | null
  country: string | null
  createdAt: string
  lastLoginAt: string | null
}

type UsernameStatus = 'idle' | 'checking' | 'available' | 'taken' | 'invalid'

interface AuthState {
  user: AuthUser | null
  isLoading: boolean
  error: string | null
  usernameStatus: UsernameStatus

  initialize: () => Promise<void>
  register: (username: string, email: string, password: string) => Promise<boolean>
  login: (email: string, password: string) => Promise<boolean>
  loginWithGoogle: () => Promise<void>
  logout: () => Promise<void>
  resetPassword: (email: string) => Promise<boolean>
  updateEmail: (newEmail: string) => Promise<boolean>
  updatePassword: (currentPassword: string, newPassword: string) => Promise<boolean>
  updateProfile: (data: Partial<Pick<AuthUser, 'username' | 'street' | 'zip' | 'city' | 'country'>>) => Promise<boolean>
  checkUsername: (username: string) => Promise<void>
  clearError: () => void
}

const USERNAME_REGEX = /^[a-zA-Z0-9]{1,10}$/
const PROFILE_CACHE_KEY = 'qr-auth-profile'

function mapUser(data: Record<string, unknown>): AuthUser {
  return {
    id:          data.id as string,
    email:       (data.email as string) ?? '',
    username:    data.username as string,
    avatarUrl:   (data.avatar_url as string)   ?? null,
    street:      (data.street as string)       ?? null,
    zip:         (data.zip as string)          ?? null,
    city:        (data.city as string)         ?? null,
    country:     (data.country as string)      ?? null,
    createdAt:   data.created_at as string,
    lastLoginAt: (data.last_login_at as string) ?? null,
  }
}

function loadCachedProfile(): AuthUser | null {
  try {
    const raw = localStorage.getItem(PROFILE_CACHE_KEY)
    return raw ? (JSON.parse(raw) as AuthUser) : null
  } catch { return null }
}

function cacheProfile(user: AuthUser): void {
  try { localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(user)) } catch { /* ignore */ }
}

function clearCachedProfile(): void {
  try { localStorage.removeItem(PROFILE_CACHE_KEY) } catch { /* ignore */ }
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user:           null,
  isLoading:      true,
  error:          null,
  usernameStatus: 'idle',

  initialize: async () => {
    const token = getToken()

    if (!token) {
      set({ isLoading: false })
      return
    }

    const cached = loadCachedProfile()
    if (cached) {
      set({ user: cached, isLoading: false })
    } else {
      set({ isLoading: false })
    }

    try {
      const timeoutPromise = new Promise<null>((resolve) =>
        setTimeout(() => resolve(null), 5000)
      )
      const fetchPromise = apiGet<Record<string, unknown>>('/profile').then(mapUser)
      const user = await Promise.race([fetchPromise, timeoutPromise])

      if (user) {
        cacheProfile(user)
        set({ user })
      }
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        clearToken()
        clearCachedProfile()
        set({ user: null })
      }
    }
  },

  register: async (username, email, password) => {
    set({ error: null, isLoading: true })

    if (!USERNAME_REGEX.test(username)) {
      set({ error: 'Invalid username', isLoading: false })
      return false
    }

    try {
      const data = await post<{ token: string; user: Record<string, unknown> }>(
        '/auth/register', { email, password, username }
      )
      setToken(data.token)
      const user = mapUser(data.user)
      cacheProfile(user)
      set({ user, isLoading: false })
      return true
    } catch (e) {
      set({ error: e instanceof Error ? e.message : String(e), isLoading: false })
      return false
    }
  },

  login: async (email, password) => {
    set({ error: null, isLoading: true })

    try {
      const data = await post<{ token: string; user: Record<string, unknown> }>(
        '/auth/login', { email, password }
      )
      setToken(data.token)
      const user = mapUser(data.user)
      cacheProfile(user)
      set({ user, isLoading: false })
      return true
    } catch (e) {
      set({ error: e instanceof Error ? e.message : String(e), isLoading: false })
      return false
    }
  },

  loginWithGoogle: async () => {
    set({ error: 'Google login is not available' })
  },

  logout: async () => {
    clearToken()
    clearCachedProfile()
    set({ user: null })
  },

  resetPassword: async (email) => {
    set({ error: null })
    try {
      await post('/auth/forgot-password', { email })
      return true
    } catch (e) {
      set({ error: e instanceof Error ? e.message : String(e) })
      return false
    }
  },

  updateEmail: async (newEmail) => {
    set({ error: null })
    try {
      const data = await put<Record<string, unknown>>('/profile', { email: newEmail })
      const user = mapUser(data)
      cacheProfile(user)
      set({ user })
      return true
    } catch (e) {
      set({ error: e instanceof Error ? e.message : String(e) })
      return false
    }
  },

  updatePassword: async (currentPassword, newPassword) => {
    set({ error: null })

    const user = get().user
    if (!user) return false

    try {
      await post('/auth/login', { email: user.email, password: currentPassword })
    } catch {
      set({ error: 'Aktuelles Passwort ist falsch' })
      return false
    }

    try {
      await put('/profile', { password: newPassword })
      return true
    } catch (e) {
      set({ error: e instanceof Error ? e.message : String(e) })
      return false
    }
  },

  updateProfile: async (data) => {
    set({ error: null })

    const user = get().user
    if (!user) return false

    try {
      const updated = await put<Record<string, unknown>>('/profile', data)
      const mapped = mapUser(updated)
      cacheProfile(mapped)
      set({ user: mapped })
      return true
    } catch (e) {
      set({ error: e instanceof Error ? e.message : String(e) })
      return false
    }
  },

  checkUsername: async (username) => {
    if (!username) {
      set({ usernameStatus: 'idle' })
      return
    }

    if (!USERNAME_REGEX.test(username)) {
      set({ usernameStatus: 'invalid' })
      return
    }

    set({ usernameStatus: 'available' })
  },

  clearError: () => set({ error: null }),
}))
